import { cn } from '@up2stay/up2promo-ui';
import { useMemo } from 'react';

import { SidebarUpgradeTeaser } from '@/core/components/billing/sidebar-upgrade-teaser';
import { useLocale } from '@/core/hooks/use-locale';
import { useNavAttentionCounts } from '@/core/hooks/use-nav-attention-counts';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

import { applySidebarNavAttentionBadges } from './nav-attention';
import { NavGroup } from './nav-group';
import { NavUser } from './nav-user';
import { createNavigationData } from './navigation-data';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from './sidebar';
import { useSidebarScrollAffordance } from './use-sidebar-scroll-affordance';
import { WorkspaceSwitcher } from './workspace-switcher';

export function AppSidebar() {
  const locale = useLocale();
  const dict = useDictionary();
  const user = useAuthStore((state) => state.user);
  const workspace = useCurrentWorkspace();
  const { workspaces, isLoading, isFetched, isError } = useWorkspaces();
  const attentionCounts = useNavAttentionCounts();
  const { ref: contentRef, hasMoreBelow } =
    useSidebarScrollAffordance<HTMLDivElement>();

  // Get user data or use fallback
  const userData = useMemo(() => {
    if (user) {
      const photoPath = user.photo?.path;
      return {
        name:
          `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.email ||
          'User',
        email: user.email || '',
        avatar:
          photoPath && photoPath.trim()
            ? getAbsoluteFileUrl(photoPath)
            : undefined,
      };
    }
    return {
      name: 'User',
      email: '',
      avatar: undefined,
    };
  }, [user]);

  const isWorkspaceScope = !!workspace;
  const { can } = usePermissions();

  const navigationData = useMemo(() => {
    const data = createNavigationData(
      locale,
      userData,
      isWorkspaceScope,
      dict.nav,
      can,
    );
    return {
      ...data,
      navGroups: applySidebarNavAttentionBadges(
        data.navGroups,
        attentionCounts,
      ),
    };
  }, [locale, userData, isWorkspaceScope, dict.nav, can, attentionCounts]);

  return (
    <Sidebar collapsible="offcanvas" variant="floating">
      <SidebarHeader>
        <WorkspaceSwitcher
          workspaces={workspaces}
          isLoading={isLoading}
          isFetched={isFetched}
          isError={isError}
        />
      </SidebarHeader>
      <SidebarContent
        ref={contentRef}
        role="navigation"
        aria-label={dict.sidebar.mainNavigation}
        data-more-below={hasMoreBelow || undefined}
        className={cn(
          hasMoreBelow &&
            '[mask-image:linear-gradient(to_bottom,black_calc(100%-2.5rem),transparent)]',
        )}
      >
        {navigationData.navGroups.map((props) => (
          <NavGroup key={props.title} {...props} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarUpgradeTeaser compactOnShortViewport />
        <NavUser user={navigationData.user} />
      </SidebarFooter>
      <SidebarRail
        aria-label={dict.sidebar.resizeSidebar}
        title={dict.sidebar.resizeSidebar}
      />
    </Sidebar>
  );
}
